/**
 * QA/Observability Panel - Display processing metrics and quality scores
 */

import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card } from '../UI/Card';
import { Badge } from '../UI/Badge';
import { Button } from '../UI/Button';
import { 
  getQADashboard, 
  getProjectMetricsSummary,
  getProcessingMetrics,
  QADashboardData,
  ProjectMetricsSummary,
} from '../../api/metrics';
import {
  TrendingUp,
  TrendingDown,
  Clock,
  CheckCircle,
  XCircle,
  Activity,
  Target,
  AlertTriangle,
  RefreshCw,
  BarChart3,
  Zap,
  Database,
} from 'lucide-react';

interface QAObservabilityPanelProps {
  projectId: string;
}

export function QAObservabilityPanel({ projectId }: QAObservabilityPanelProps) {
  const [days, setDays] = useState(30);
  const [selectedStage, setSelectedStage] = useState<string | null>(null);

  const { data: dashboardData, isLoading: loadingDashboard, refetch: refetchDashboard } = useQuery({
    queryKey: ['qa-dashboard', projectId, days],
    queryFn: () => getQADashboard(projectId, days),
    enabled: !!projectId,
  });

  const { data: projectSummary, isLoading: loadingSummary } = useQuery({
    queryKey: ['project-metrics-summary', projectId],
    queryFn: () => getProjectMetricsSummary(projectId),
    enabled: !!projectId,
  });

  if (loadingDashboard || loadingSummary) {
    return (
      <div className="text-center py-12">
        <Activity className="h-8 w-8 text-gray-400 mx-auto mb-4 animate-spin" />
        <p className="text-gray-500">Loading metrics...</p>
      </div>
    );
  }

  if (!dashboardData || !dashboardData.summary || !projectSummary) {
    return (
      <div className="text-center py-12">
        <AlertTriangle className="h-8 w-8 text-gray-400 mx-auto mb-4" />
        <p className="text-gray-500">No metrics data available</p>
        {dashboardData && !dashboardData.summary && (
          <p className="text-sm text-gray-400 mt-2">Dashboard data structure incomplete</p>
        )}
      </div>
    );
  }

  const { summary, latency_breakdown, quality_metrics, stage_metrics, entity_extraction_stats } = dashboardData;
  
  // Ensure all required fields exist with defaults
  const safeSummary = {
    total_jobs: summary?.total_jobs || 0,
    total_artifacts: summary?.total_artifacts || 0,
    total_chunks: summary?.total_chunks || 0,
    total_entities: summary?.total_entities || 0,
    avg_faithfulness: summary?.avg_faithfulness || 0,
    avg_hallucination_rate: summary?.avg_hallucination_rate || 0,
    avg_recall: summary?.avg_recall || 0,
    avg_precision: summary?.avg_precision || 0,
  };
  
  const safeLatencyBreakdown = latency_breakdown || [];
  const safeQualityMetrics = quality_metrics || [];
  const safeStageMetrics = stage_metrics || [];
  const safeEntityStats = entity_extraction_stats || [];

  // Helper to format duration
  const formatDuration = (ms: number) => {
    if (ms < 1000) return `${ms.toFixed(0)}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    return `${(ms / 60000).toFixed(1)}m`;
  };

  // Helper to get quality score color
  const getQualityColor = (score: number, reverse: boolean = false) => {
    const normalized = reverse ? 1 - score : score;
    if (normalized >= 0.8) return 'text-green-600 bg-green-50';
    if (normalized >= 0.6) return 'text-yellow-600 bg-yellow-50';
    return 'text-red-600 bg-red-50';
  };

  return (
    <div className="space-y-6">
      {/* Header with refresh */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">QA & Observability</h2>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Processing quality metrics and performance monitoring
          </p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
          >
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
            <option value={180}>Last 180 days</option>
          </select>
          <Button
            size="sm"
            variant="outline"
            onClick={() => refetchDashboard()}
          >
            <RefreshCw className="h-4 w-4" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Total Jobs</div>
              <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {safeSummary.total_jobs.toLocaleString()}
              </div>
            </div>
            <Activity className="h-8 w-8 text-blue-600" />
          </div>
        </Card>

        <Card>
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Artifacts Processed</div>
              <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {safeSummary.total_artifacts.toLocaleString()}
              </div>
            </div>
            <Database className="h-8 w-8 text-green-600" />
          </div>
        </Card>

        <Card>
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Chunks Created</div>
              <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {safeSummary.total_chunks.toLocaleString()}
              </div>
            </div>
            <BarChart3 className="h-8 w-8 text-purple-600" />
          </div>
        </Card>

        <Card>
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Entities Extracted</div>
              <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {safeSummary.total_entities.toLocaleString()}
              </div>
            </div>
            <Target className="h-8 w-8 text-orange-600" />
          </div>
        </Card>
      </div>

      {/* Quality Metrics */}
      <Card title="Quality Scores">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className={`p-4 rounded-lg ${getQualityColor(safeSummary.avg_faithfulness / 100)}`}>
            <div className="text-sm font-medium mb-1">Faithfulness</div>
            <div className="text-2xl font-bold">{safeSummary.avg_faithfulness.toFixed(1)}</div>
            <div className="text-xs mt-1">0-100 score</div>
          </div>

          <div className={`p-4 rounded-lg ${getQualityColor(safeSummary.avg_hallucination_rate, true)}`}>
            <div className="text-sm font-medium mb-1">Hallucination Rate</div>
            <div className="text-2xl font-bold">{(safeSummary.avg_hallucination_rate * 100).toFixed(2)}%</div>
            <div className="text-xs mt-1">Lower is better</div>
          </div>

          <div className={`p-4 rounded-lg ${getQualityColor(safeSummary.avg_recall)}`}>
            <div className="text-sm font-medium mb-1">Recall</div>
            <div className="text-2xl font-bold">{(safeSummary.avg_recall * 100).toFixed(1)}%</div>
            <div className="text-xs mt-1">Coverage metric</div>
          </div>

          <div className={`p-4 rounded-lg ${getQualityColor(safeSummary.avg_precision)}`}>
            <div className="text-sm font-medium mb-1">Precision</div>
            <div className="text-2xl font-bold">{(safeSummary.avg_precision * 100).toFixed(1)}%</div>
            <div className="text-xs mt-1">Accuracy metric</div>
          </div>
        </div>
      </Card>

      {/* Latency Breakdown */}
      <Card title="Latency Breakdown by Stage">
        <div className="space-y-3">
          {safeLatencyBreakdown.length > 0 ? (
            safeLatencyBreakdown.map((stage) => (
            <div key={stage.stage} className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Zap className="h-4 w-4 text-blue-600" />
                  <span className="font-medium text-gray-900 dark:text-gray-100 capitalize">
                    {stage.stage.replace('_', ' ')}
                  </span>
                </div>
                <Badge variant="default">
                  Avg: {formatDuration(stage.avg_duration_ms)}
                </Badge>
              </div>
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div>
                  <div className="text-gray-500">P50</div>
                  <div className="font-medium">{formatDuration(stage.p50_duration_ms)}</div>
                </div>
                <div>
                  <div className="text-gray-500">P95</div>
                  <div className="font-medium">{formatDuration(stage.p95_duration_ms)}</div>
                </div>
                <div>
                  <div className="text-gray-500">P99</div>
                  <div className="font-medium">{formatDuration(stage.p99_duration_ms)}</div>
                </div>
              </div>
            </div>
          ))
          ) : (
            <div className="text-center py-8 text-gray-500">No latency data available</div>
          )}
        </div>
      </Card>

      {/* Stage Metrics */}
      <Card title="Stage Performance">
        <div className="overflow-x-auto">
          {safeStageMetrics.length > 0 ? (
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-700 dark:text-gray-300">Stage</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-gray-700 dark:text-gray-300">Processed</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-gray-700 dark:text-gray-300">Failed</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-gray-700 dark:text-gray-300">Success Rate</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-gray-700 dark:text-gray-300">Avg Duration</th>
                </tr>
              </thead>
              <tbody>
                {safeStageMetrics.map((stage) => {
                // Backend already returns success_rate as percentage (0-100), don't multiply by 100
                const successRate = stage.success_rate;
                return (
                  <tr key={stage.stage} className="border-b border-gray-100 hover:bg-gray-50 dark:hover:bg-slate-800">
                    <td className="py-3 px-4">
                      <span className="font-medium text-gray-900 dark:text-gray-100 capitalize">
                        {stage.stage.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="text-right py-3 px-4 text-gray-700 dark:text-gray-300">
                      {stage.total_processed.toLocaleString()}
                    </td>
                    <td className="text-right py-3 px-4 text-gray-700 dark:text-gray-300">
                      {stage.total_failed.toLocaleString()}
                    </td>
                    <td className="text-right py-3 px-4">
                      <Badge
                        variant={successRate >= 95 ? 'success' : successRate >= 80 ? 'default' : 'error'}
                      >
                        {successRate.toFixed(1)}%
                      </Badge>
                    </td>
                    <td className="text-right py-3 px-4 text-gray-700 dark:text-gray-300">
                      {formatDuration(stage.avg_duration_ms)}
                    </td>
                  </tr>
                );
                })}
              </tbody>
            </table>
          ) : (
            <div className="text-center py-8 text-gray-500">No stage metrics available</div>
          )}
        </div>
      </Card>

      {/* Entity Extraction Stats */}
      {safeEntityStats.length > 0 && (
        <Card title="Entity Extraction Statistics">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {safeEntityStats.map((stat) => (
              <div key={stat.entity_type} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-gray-900 dark:text-gray-100 capitalize">
                    {stat.entity_type.replace(/_/g, ' ')}
                  </span>
                  <Badge variant="default">{stat.count.toLocaleString()}</Badge>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Quality Trends (if available) */}
      {safeQualityMetrics.length > 0 && (
        <Card title="Quality Trends">
          <div className="space-y-2">
            {safeQualityMetrics.slice(-10).reverse().map((metric, idx) => (
              <div key={idx} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {new Date(metric.date).toLocaleDateString()}
                </div>
                <div className="flex items-center gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">Faithfulness: </span>
                    <span className="font-medium">{metric.faithfulness.toFixed(1)}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Hallucination: </span>
                    <span className="font-medium">{(metric.hallucination_rate * 100).toFixed(2)}%</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Recall: </span>
                    <span className="font-medium">{(metric.recall * 100).toFixed(1)}%</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Precision: </span>
                    <span className="font-medium">{(metric.precision * 100).toFixed(1)}%</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}

